import { useCallback, useMemo } from 'react'
import { ResponsiveLine } from '@nivo/line'
import { BasicTooltip } from '@nivo/tooltip'
import { Trans } from 'react-i18next'

import type { Point } from '@nivo/line'
import { type Emissions, type ChartDataPoint } from 'types'
import { DataType } from 'types/data'

import './style.scss'

type Props = {
  data: ChartDataPoint[] | ChartDataPoint[][]
  labelX: string
  labelY: string
  emissions: Emissions[]
  dataType: DataType
}

const CHART_COLORS = [
  '#4a4ea6',
  '#bdbdbd',
  '#b39ddb',
  '#444654',
  '#d6ccff',
  '#7e57c2',
  '#8c9eff',
  '#a7bfff',
  '#5c6bc0',
  '#ccc',
  '#040444',
  '#d1c4e9',
  '#757575'
]

export default function LineChartNew({
  data,
  labelX,
  labelY,
  emissions,
  dataType
}: Props) {
  const formatDate = useCallback(
    (date: Date, hideMonth: boolean = false): string => {
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        ...(hideMonth ? null : { month: 'long' }),
        timeZone: 'UTC'
      }
      return new Intl.DateTimeFormat('en-EN', options).format(date)
    },
    []
  )

  const polygonIds = emissions.map((e) => e.feature.properties.feature_uuid)
  const chartData = data.map((d, index) => {
    return {
      id: polygonIds[index],
      data: Array.isArray(d)
        ? d.map((point: ChartDataPoint) => ({
            x: formatDate(point.x as Date),
            y: point.y
          }))
        : d
    }
  })

  const yearTicks = useMemo(() => {
    const seenYears = new Set<number>()
    const ticks: string[] = []

    chartData.forEach((series) => {
      const points = Array.isArray(series.data) ? series.data : [series.data]
      points.forEach((point) => {
        const parts = String(point.x).split(' ')
        const year = Number(parts[parts.length - 1])
        if (!seenYears.has(year)) {
          seenYears.add(year)
          ticks.push(String(point.x))
        }
      })
    })
    return ticks
  }, [chartData])

  const Tooltip = ({ point }: { point: Point<any> }) => {
    const {
      country_code: countryCode,
      land_cover: landCover,
      site_name: siteName,
      area
    } = emissions[point.seriesIndex].feature.properties
    const { xFormatted: date, yFormatted: nee } = point.data

    const unitPrefix = Math.abs(Number(nee)) > 1000 ? 'kt' : 't'
    const unit =
      dataType === DataType.Intensity
        ? `${unitPrefix}CO₂/ha`
        : `${unitPrefix}CO₂`
    const formattedNee = `${(Math.abs(Number(nee)) > 1000
      ? Number(nee) / 1000
      : Number(nee)
    ).toFixed(2)} ${unit}`

    return (
      <BasicTooltip
        id={
          <div className="cst-line-chart-tooltip" style={{ minWidth: 80 }}>
            <div>
              <Trans
                i18nKey="line_chart.tooltip_country"
                values={{ countryCode }}
              />
            </div>
            <div>
              <Trans
                i18nKey="line_chart.tooltip_land_cover"
                values={{ landCover }}
              />
            </div>
            {siteName != null && (
              <div>
                <Trans
                  i18nKey="line_chart.tooltip_site_name"
                  values={{ siteName }}
                />
              </div>
            )}
            <div>
              <Trans
                i18nKey="line_chart.tooltip_polygon_name"
                values={{ polygonName: point.seriesId }}
              />
            </div>
            <div>
              <Trans
                i18nKey="line_chart.tooltip_area"
                values={{ area: area.toFixed(2) }}
              />
            </div>
            <div>
              <Trans
                i18nKey="line_chart.tooltip_nee"
                values={{ nee: formattedNee }}
              />
            </div>
            <div>
              <Trans i18nKey="line_chart.tooltip_date" values={{ date }} />
            </div>
          </div>
        }
      />
    )
  }

  return (
    <>
      <div className="cst-line-chart-new-wrapper">
        <ResponsiveLine
          data={chartData as any}
          colors={CHART_COLORS}
          yScale={{
            type: 'linear',
            min: 'auto',
            max: 'auto',
            stacked: false,
            reverse: false
          }}
          margin={{ top: 72, right: 72, bottom: 72, left: 92 }}
          axisBottom={{
            legend: labelX,
            legendOffset: 52,
            tickValues: yearTicks,
            tickSize: 0,
            format: (value) => {
              const parts = value.split(' ')
              return parts[parts.length - 1]
            }
          }}
          axisLeft={{ legend: labelY, legendOffset: -72 }}
          pointSize={4}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'seriesColor' }}
          pointLabelYOffset={-12}
          enableTouchCrosshair={true}
          useMesh={true}
          tooltip={({ point }) => <Tooltip point={point} />}
          // @ts-expect-error adding zIndex to wrapper or container doesn't work, https://github.com/plouc/nivo/issues/2490
          theme={{ tooltip: { zIndex: 99999999 } }}
        />
      </div>
      {chartData.length > 1 && (
        <div className="cst-line-chart-legend">
          {chartData.map((series, i) => (
            <span key={series.id}>
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: CHART_COLORS[i % CHART_COLORS.length],
                  marginRight: 6
                }}
              />
              {series.id}
            </span>
          ))}
        </div>
      )}
    </>
  )
}
